import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import { OUTBOX_POST_GENERATION_NAV_STATE } from '@/lib/outboxPostGenerationNav';
import { logAccountInteraction } from '@/components/utils/analytics';
import {
  describeSequencesFailed,
  describeTerminalProgress,
  describeDiyGenerationTimeout,
  diyGenerationPollDecision,
  diyGenerationSafetyCapMs,
  fetchPlaybookStatusBodiesAllSettled,
  formatDraftsReadyLine,
  formatGeneratingProgressLine,
  parsePlaybookStatusData,
  normalizeRunSequenceIds,
  nextConsecutivePollFailures,
  describeStatusPollHttpError,
} from '@/lib/playbookStatusAggregate';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

/** Time between poll cycles. Keep in the 3–5s contract; expected_seconds is not a stop. */
export const DIY_GENERATION_POLL_TICK_MS = 4000;

export { diyGenerationSafetyCapMs, describeDiyGenerationTimeout } from '@/lib/playbookStatusAggregate';

/** Surface a real error after this many consecutive all-failed poll ticks. */
export const DIY_GENERATION_POLL_ERROR_STREAK = 3;

let activeGenerationPollCount = 0;

export function getActiveDiyGenerationPollCount() {
  return activeGenerationPollCount;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function goToOutbox(navigate) {
  navigate(createPageUrl('Outbox'), { state: OUTBOX_POST_GENERATION_NAV_STATE });
}

function logGenerated(analytics) {
  if (!analytics?.tenantId || !analytics?.userId) return;
  void logAccountInteraction({
    tenantId: analytics.tenantId,
    userId: analytics.userId,
    playbookId: analytics.playbookId,
    accountId: analytics.accountId,
    personId: analytics.personId,
    source: 'diy',
    interactionType: 'playbook_generated',
    metadata: analytics.metadata,
  }).catch((err) => console.error('Analytics error:', err));
}

/**
 * Polls GET /api/playbooks/status/:sequenceId for every run sequence id until all
 * report status:complete, a terminal failure, or the safety cap.
 * Survives DIY page unmount. One Sonner id from start through settle.
 */
export async function runDiyPlaybookGenerationPoll({
  tenantId,
  userId,
  sequenceIds,
  expectedSeconds,
  startedAt,
  stepsCreated,
  navigate,
  onSettled,
  analytics,
}) {
  const ids = normalizeRunSequenceIds({ sequence_ids: sequenceIds });
  const emptyResult = { ok: true, completedInPoll: false, timedOut: false };

  if (!ids.length) {
    onSettled?.(emptyResult);
    return emptyResult;
  }

  activeGenerationPollCount++;
  const toastId = `diy-generation-${crypto.randomUUID()}`;
  let settled = { ok: false, completedInPoll: false, timedOut: false };

  try {
    const parsedStarted = startedAt ? Date.parse(startedAt) : NaN;
    const startedAtMs = Number.isFinite(parsedStarted) ? parsedStarted : Date.now();
    const safeExpected =
      Number.isFinite(Number(expectedSeconds)) && Number(expectedSeconds) > 0
        ? Number(expectedSeconds)
        : 300;
    const expectedTotal = Math.max(0, Math.floor(Number(stepsCreated) || 0));
    const capMs = diyGenerationSafetyCapMs(safeExpected);
    const lastBodyById = {};
    const hint = safeExpected ? `Usually takes about ${Math.round(safeExpected)}s` : undefined;
    let consecutiveFailures = 0;

    toast.loading(
      formatGeneratingProgressLine({ complete: 0, total: 0 }, safeExpected, expectedTotal),
      {
        id: toastId,
        duration: Infinity,
        description: hint,
      }
    );

    for (;;) {
      if (Date.now() - startedAtMs >= capMs) {
        toast.warning(describeDiyGenerationTimeout(), { id: toastId, duration: 10000 });
        goToOutbox(navigate);
        settled = { ok: false, timedOut: true, completedInPoll: false };
        return settled;
      }

      const statusBodies = await fetchPlaybookStatusBodiesAllSettled(
        API_BASE_URL,
        ids,
        tenantId,
        userId,
        lastBodyById
      );
      const parsedList = statusBodies.map(parsePlaybookStatusData);
      const decision = diyGenerationPollDecision(parsedList);
      const failedCount = Number(statusBodies.failedCount) || 0;
      consecutiveFailures = nextConsecutivePollFailures(
        consecutiveFailures,
        failedCount,
        ids.length
      );

      console.log('DIY generation poll', {
        complete: decision.agg.complete,
        total: decision.agg.total,
        action: decision.action,
        failedCount,
        consecutiveFailures,
      });

      if (decision.action === 'failed') {
        toast.error('Generation failed', {
          id: toastId,
          description: describeSequencesFailed(
            ids.length,
            decision.failedSeq,
            decision.agg.complete
          ),
        });
        goToOutbox(navigate);
        settled = { ok: false, timedOut: false, completedInPoll: false };
        return settled;
      }

      if (decision.action === 'no_steps') {
        toast.error('No steps to generate', {
          id: toastId,
          description: `${decision.noStepsSeq} sequence${decision.noStepsSeq === 1 ? '' : 's'} had no steps.`,
        });
        goToOutbox(navigate);
        settled = { ok: false, timedOut: false, completedInPoll: false };
        return settled;
      }

      if (decision.action === 'complete') {
        const agg = decision.agg;
        logGenerated(analytics);
        toast.success(formatDraftsReadyLine(agg, expectedTotal), {
          id: toastId,
          duration: 4000,
          description: 'Opening Outbox…',
        });
        goToOutbox(navigate);
        settled = { ok: true, timedOut: false, issues: false, completedInPoll: true };
        return settled;
      }

      // [2026-10-05] Generation stopped early and nothing will resume it —
      // almost always a deploy that drained the instance running the background
      // generation. Previously this state was indistinguishable from "still
      // queued", so the toaster span forever and the user was told nothing.
      // Shown as INFO rather than an error: nothing is broken, nothing is lost,
      // and the drafts already created are saved.
      if (decision.action === 'interrupted') {
        const mins = Math.max(1, Math.round((decision.retryAfterSeconds || 300) / 60));
        toast.info('Generation paused before it finished', {
          id: toastId,
          duration: 12000,
          description:
            decision.userMessage ||
            `Some drafts were not created because the platform was updating. ` +
            `Nothing has been lost — the drafts already created are saved. ` +
            `Please try the rest again in about ${mins} minutes.`,
        });
        goToOutbox(navigate);
        settled = { ok: false, timedOut: false, completedInPoll: false, interrupted: true };
        return settled;
      }

      if (decision.action === 'partial_failure') {
        const agg = decision.agg;
        toast.warning('Generation did not finish cleanly', {
          id: toastId,
          description: describeTerminalProgress(agg, decision.blockedSteps),
        });
        goToOutbox(navigate);
        settled = { ok: false, timedOut: false, completedInPoll: false, issues: true };
        return settled;
      }

      if (consecutiveFailures >= DIY_GENERATION_POLL_ERROR_STREAK) {
        toast.error('Could not check generation status', {
          id: toastId,
          description: describeStatusPollHttpError(statusBodies.failedStatuses),
        });
        goToOutbox(navigate);
        settled = { ok: false, timedOut: false, completedInPoll: false };
        return settled;
      }

      const progressLine = formatGeneratingProgressLine(
        decision.agg,
        safeExpected,
        expectedTotal
      );
      toast.loading(progressLine, {
        id: toastId,
        duration: Infinity,
        description: failedCount
          ? 'Still checking generation status…'
          : hint,
      });

      await sleep(DIY_GENERATION_POLL_TICK_MS);
    }
  } catch (e) {
    console.error('runDiyPlaybookGenerationPoll', e);
    toast.error('Could not check generation status', {
      id: toastId,
      description: e?.message || 'Open Outbox to see whether drafts are ready.',
    });
    try {
      goToOutbox(navigate);
    } catch {
      // navigate may be unavailable if the router already unmounted
    }
    settled = { ok: false, timedOut: false, completedInPoll: false };
    return settled;
  } finally {
    activeGenerationPollCount = Math.max(0, activeGenerationPollCount - 1);
    onSettled?.(settled);
  }
}
